import { useEstimate } from '../context/EstimateContext'
import { COMPANY, TIME_WINDOWS } from '../config/constants'
import { maskPhone, priceRange } from '../lib/format'
import { CheckCircle } from '../components/icons'

export default function ConfirmationStep() {
  const { confirmation, booking, estimate, chosenOptionType, reset } = useEstimate()

  const chosen =
    estimate?.options?.find((o) => o.type === chosenOptionType) || estimate?.options?.[0] || null
  const window = TIME_WINDOWS.find((w) => w.key === booking?.timeWindow)

  return (
    <div className="animate-fade-in py-4">
      <div className="flex flex-col items-center text-center">
        <div className="flex h-[68px] w-[68px] items-center justify-center rounded-full bg-emerald-100 text-emerald-600 shadow-glass animate-pop-in">
          <CheckCircle className="h-9 w-9" />
        </div>
        <h1 className="mt-5 text-[26px] font-bold text-ink">You&apos;re booked!</h1>
        <p className="mt-2 max-w-xs text-sm leading-relaxed text-muted">
          We&apos;ve got your request. A West Boca technician will reach out to{' '}
          <span className="font-semibold text-subink">{maskPhone(booking?.phone)}</span> to lock in
          your exact appointment time.
        </p>
      </div>

      <div className="mt-7 glass space-y-3 p-5">
        <Row label="Confirmation #" value={confirmation?.jobId || '—'} mono />
        {chosen && (
          <Row
            label="Service"
            value={
              <span className="capitalize">
                {chosen.type} — <span className="font-normal text-muted">{chosen.label}</span>
              </span>
            }
          />
        )}
        {chosen && (
          <Row
            label="Estimate"
            value={`${priceRange(chosen.totalLow, chosen.totalHigh)} ${chosen.type === 'replace' ? 'installed' : 'total'}`}
          />
        )}
        {window && <Row label="Preferred time" value={`${window.label} (${window.detail})`} />}
        {booking?.address && <Row label="Address" value={booking.address} />}
      </div>

      <div className="mt-5 rounded-3xl bg-brand-soft p-5">
        <p className="font-display text-sm font-semibold text-ink">What happens next</p>
        <ol className="mt-3 space-y-2.5 text-sm text-subink">
          <li className="flex gap-2.5">
            <span className="font-bold text-brand-600">1.</span> We reach out to confirm your appointment window.
          </li>
          <li className="flex gap-2.5">
            <span className="font-bold text-brand-600">2.</span> Your technician confirms the final price on-site — before any work begins.
          </li>
          <li className="flex gap-2.5">
            <span className="font-bold text-brand-600">3.</span> You pay only when the job&apos;s done and you&apos;re happy.
          </li>
        </ol>
      </div>

      {confirmation?.mock && (
        <p className="mt-5 rounded-2xl bg-amber-50 p-2.5 text-center text-[11px] text-amber-700">
          Demo mode — no real booking was created (backend not configured).
        </p>
      )}

      <a href={`tel:${COMPANY.phone.replace(/[^\d+]/g, '')}`} className="btn-secondary mt-6 block">
        Call us: {COMPANY.phone}
      </a>
      <button onClick={reset} className="mt-3 w-full py-2 text-center text-sm font-medium text-muted transition hover:text-subink">
        Start a new estimate
      </button>
    </div>
  )
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <span className="shrink-0 text-muted">{label}</span>
      <span className={`text-right font-semibold text-ink ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  )
}
